'use client'

import { useSession } from 'next-auth/react'
import { toast } from 'sonner'
import { getCart } from '@/lib/actions'
import { useAppDispatch, useAppSelector, useModals } from '@/lib/hooks'
import {
  resetCart as resetCartAction,
  selectProduct as selectProductAction,
  setCart as setCartAction,
} from '@/lib/redux'

interface AddToCartPayload {
  product: CartProduct
  cartId: string
}

interface RemoveProductPayload {
  productId: string | number
  cartId: string
}

interface UpdateCartProductPayload {
  productId: string | number
  cartId: string
  quantity?: number
}

interface Cart {
  cart: any
  totalPayable: any
  selectProduct(product: Product): void
  resetCart(): void
  removeProduct(payload: RemoveProductPayload): void
  addToCart(payload: AddToCartPayload): void
  addToCartByBarcode(payload: any): void
  getAndUpdateCart(cartId: string): void
  updateCartProduct(payload: UpdateCartProductPayload): void
  updateKotCartItem(payload: any, cb?: any): void
  setCart(cart: any): void
  addCartDiscount(payload: any): void
  setProduct(product: Product): void
  addSerialNumber(payload: any): void
}

export const useCart = (): Cart => {
  const { data: session } = useSession() as any
  const dispatch = useAppDispatch()
  const { openModal, closeModal } = useModals()
  const cart = useAppSelector((state) => state.cart)

  /*
   * Reset the cart state in the redux store
   */
  const resetCart = () => {
    dispatch(resetCartAction())
  }

  /*
   * Set the cart state in the redux store
   */
  const setCart = (cart: any) => {
    dispatch(setCartAction(cart))
  }

  /*
   * Get and update the cart
   */
  const getAndUpdateCart = async (cartId: string) => {
    try {
      const cart = await getCart(cartId)
      dispatch(setCartAction(cart))
      return cart
    } catch (error: any) {
      toast.error('Failed to get cart')
    }
  }

  /*
   * Select a product and open the add to cart modal
   * Selecting a product will set the selected product in the redux store
   */
  const selectProduct = (product: Product) => {
    dispatch(selectProductAction(product))

    if (!product?.quantity) {
      openModal('ADD_TO_CART')
    } else {
      openModal('EDIT_CART_ITEM')
    }
  }

  const setProduct = (product: Product) => {
    dispatch(selectProductAction(product))
  }

  /*
   * Add a product to the cart
   */
  const addToCart = async (payload: AddToCartPayload) => {
    const cartWorker = new Worker(
      new URL('@/lib/workers/cart.worker', import.meta.url),
    )
    cartWorker.postMessage({ payload, type: 'ADD_TO_CART', session })
    cartWorker.onmessage = async (event) => {
      const { type, error, data } = event.data
      if (type === 'ERROR') {
        toast.error(error)
        return
      }

      dispatch(setCartAction(data))
      toast.success('Product added to cart')
      cartWorker.terminate()
    }
    cartWorker.onerror = (error) => {
      console.error('Worker error:', error)
      toast.error('An error occurred while adding the product')
      cartWorker.terminate()
    }
    closeModal('ADD_TO_CART')
  }

  /*
   * Add a product to the cart using barcode scanner
   */
  const addToCartByBarcode = (payload: string) => {
    const cartWorker = new Worker(
      new URL('@/lib/workers/cart.worker', import.meta.url),
    )
    cartWorker.postMessage({ payload, type: 'ADD_TO_CART_BY_BARCODE', session })
    cartWorker.onmessage = (event) => {
      const { type, error, data } = event.data

      try {
        if (type === 'SHOW_ALERT') {
          toast.warning(data)
        } else if (type === 'ERROR') {
          toast.error(error)
        } else if (type === 'ADD_TO_CART_HAS_MULTIPLE') {
          selectProduct(data)
        } else {
          dispatch(setCartAction(data))
          toast.success('Barcode product added to cart')
        }
      } finally {
        cartWorker.terminate()
      }
    }

    cartWorker.onerror = (error) => {
      console.error('Worker error:', error)
      toast.error('An error occurred while processing the barcode')
      cartWorker.terminate()
    }
  }

  /*
   * Remove a product from the cart
   */
  const removeProduct = (payload: RemoveProductPayload) => {
    const cartWorker = new Worker(
      new URL('@/lib/workers/cart.worker', import.meta.url),
    )
    cartWorker.postMessage({
      payload,
      type: 'REMOVE_PRODUCT_FROM_CART',
      session,
    })
    cartWorker.onmessage = async (event) => {
      const { type, error, data } = event.data
      try {
        if (type === 'ERROR') {
          toast.error(error)
        } else {
          dispatch(setCartAction(data))
          toast.success('Product removed from cart')
        }
      } finally {
        cartWorker.terminate()
      }
    }
    cartWorker.onerror = (error) => {
      console.error('Worker error:', error)
      toast.error('An error occurred while removing the product')
      cartWorker.terminate()
    }
  }

  /*
   * Add a cart discount
   */
  const addCartDiscount = (payload: any) => {
    const cartWorker = new Worker(
      new URL('@/lib/workers/cart.worker', import.meta.url),
    )
    cartWorker.postMessage({
      payload,
      type: 'ADD_CART_DISCOUNT',
      session,
    })
    cartWorker.onmessage = async (event) => {
      const { type, error, data } = event.data
      try {
        if (type === 'ERROR') {
          toast.error(error)
        } else {
          dispatch(setCartAction(data))
          toast.success('Cart discount added')
        }
      } finally {
        cartWorker.terminate()
      }
    }
    cartWorker.onerror = (error) => {
      console.error('Worker error:', error)
      toast.error('An error occurred while adding the cart discount')
      cartWorker.terminate()
    }
  }

  /*
   * Update a product in the cart
   */
  const updateCartProduct = (payload: any) => {
    const cartWorker = new Worker(
      new URL('@/lib/workers/cart.worker', import.meta.url),
    )
    cartWorker.postMessage({ payload, type: 'UPDATE_CART_PRODUCT', session })
    cartWorker.onmessage = (event) => {
      const { type, error, data } = event.data
      if (type === 'ERROR') {
        toast.error(error)
        return
      }

      dispatch(setCartAction(data))
      toast.success('Product updated in cart')
      cartWorker.terminate()
    }
  }

  /*
   * Add a serial number to a product in the cart
   */
  const addSerialNumber = (payload: any) => {
    const cartWorker = new Worker(
      new URL('@/lib/workers/cart.worker', import.meta.url),
    )

    cartWorker.postMessage({
      payload,
      type: 'ADD_SERIAL_NUMBER_TO_CART_PRODUCT',
      session,
    })

    cartWorker.onmessage = async (event) => {
      const { type, error, data } = event.data
      if (type === 'ERROR') {
        toast.error(error)
        return
      }

      await getAndUpdateCart(payload?.cartId)

      toast.success('Serial number added to product')
      cartWorker.terminate()
    }
  }

  /*
   * Update a KOT item in the cart
   */
  const updateKotCartItem = (payload: any, cb?: any) => {
    const cartWorker = new Worker(
      new URL('@/lib/workers/cart.worker', import.meta.url),
    )
    cartWorker.postMessage({ payload, type: 'UPDATE_KOT_CART_ITEM', session })
    cartWorker.onmessage = (event) => {
      const { type, error, data } = event.data
      if (type === 'ERROR') {
        toast.error(error)
        return
      }

      dispatch(setCartAction(data))
      cb?.()
      toast.success('Item updated.')
      cartWorker.terminate()
    }
  }

  /* Total Payable */
  const totalPayable = cart?.cart_totals?.find(
    (line: any) => line?.type === 'final_payble_amount',
  )?.amount

  return {
    cart: cart,
    totalPayable,
    selectProduct,
    setProduct,
    resetCart,
    removeProduct,
    addToCart,
    getAndUpdateCart,
    addToCartByBarcode,
    updateCartProduct,
    updateKotCartItem,
    setCart,
    addCartDiscount,
    addSerialNumber,
  }
}
